import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AI_QUOTA_USAGE_STATUS, type AIQuotaUsageStatus, useAIQuota } from 'core/hooks/useAIQuota';
import type { TagColor } from 'ui';

type Params = {
  isTeacher?: boolean;
};

type TagColors = {
  tagColor: TagColor;
  textColor: string;
};

type ChatQuotaUsageView = TagColors & {
  label: string;
  tooltip?: string;
};

// The pastel Tag paints its text grey-900; the Figma chip uses the -800 of its own colour.
const TAG_COLORS_BY_STATUS: Record<AIQuotaUsageStatus, TagColors> = {
  [AI_QUOTA_USAGE_STATUS.UNUSED]: { tagColor: 'pastel-blue', textColor: 'var(--ui-color-blue-800)' },
  [AI_QUOTA_USAGE_STATUS.IN_USE]: { tagColor: 'pastel-grey', textColor: 'var(--ui-color-neutral-grey-800)' },
  [AI_QUOTA_USAGE_STATUS.EXHAUSTED]: { tagColor: 'pastel-red', textColor: 'var(--ui-color-red-800)' },
};

/**
 * The student's chip re-reads the quota every time it mounts, which is every time the chat opens: the
 * value may have been spent from another tab since the last turn this one saw. The teacher's does not:
 * each teacher tools screen mounts its own provider together with the chip, and that provider already
 * reads it.
 */
function useChatQuotaUsage({ isTeacher = false }: Params = {}): ChatQuotaUsageView | null {
  const { t } = useTranslation(['chatbot']);
  const { refreshQuota, usage } = useAIQuota();

  useEffect(() => {
    if (!isTeacher) {
      refreshQuota();
    }
  }, [isTeacher, refreshQuota]);

  if (!usage) {
    return null;
  }

  return {
    label: t('chatbot:quota_usage_percent', { percent: usage.percent }),
    tooltip: isTeacher ? t('chatbot:quota_usage_tooltip', { percent: usage.percent }) : undefined,
    ...TAG_COLORS_BY_STATUS[usage.status],
  };
}

export default useChatQuotaUsage;
